import React from "react";
import {
  findOceanPlace, oceanPlaces, placeCollections, placeCategoryLabels,
  getPlaceFacts, getPlaceFaqs, getPlaceMapUrl, getRelatedPlaces, getPlaceGuideLinks,
} from "../utils/oceanPlacePages.mjs";

// Shared by React and the build-time renderer: crawlers receive the same body.
const h = React.createElement;
const link = (href, text, props = {}) => h("a", { href, ...props }, text);
const external = { target: "_blank", rel: "noopener noreferrer" };
const tags = (place) => h("div", { className: "op-tags" },
  h("span", null, place.area),
  ...place.categories.map((key) => h("span", { key }, placeCategoryLabels[key])));

function PlaceCard({ place }) {
  return link(place.path, [
    place.image
      ? h("img", { key: "image", src: place.image, alt: place.name, loading: "lazy", width: 480, height: 300 })
      : h("div", { key: "placeholder", className: "op-card-placeholder", "aria-hidden": true }, h("span", null, place.genre || "宮古島のお店")),
    h("div", { key: "body", className: "op-card-body" },
      h("small", null, place.area),
      h("h3", null, place.name),
      h("p", null, place.description),
      place.collection === "restaurants" && h("div", { className: "op-card-meta" },
        h("span", null, place.hours || "営業時間は店舗に確認"),
        place.priceRange && h("span", null, place.priceRange)),
      h("span", { className: "op-card-more" }, "詳細を見る", h("span", { "aria-hidden": true }, "↗"))),
  ], { className: "op-card" });
}

function PlaceDirectory({ collection, query, category, onQueryChange, onCategoryChange }) {
  const info = placeCollections[collection];
  const entries = oceanPlaces.filter((place) => place.collection === collection);
  const categories = [...new Set(entries.flatMap((place) => place.categories))];
  const normalize = (text) => text.normalize("NFKC").toLowerCase().replace(/\s/g, "");
  const visible = entries.filter((place) =>
    (!category || place.categories.includes(category)) &&
    normalize(`${place.name} ${place.area} ${place.genre || ""}`).includes(normalize(query)));
  return h(React.Fragment, null,
    h("header", { className: "op-directory-hero" },
      h("span", { className: "op-eyebrow" }, info.eyebrow),
      h("h1", null, info.title),
      h("p", null, info.description)),
    h("section", { className: "op-filters", "aria-label": "条件で探す" },
      h("label", { className: "op-search" }, h("span", null, "名前・エリアで探す"),
        h("input", { type: "search", value: query, placeholder: "例：来間島", onChange: onQueryChange || (() => {}), "aria-label": "名前・エリアで探す" })),
      h("div", { className: "op-filter-buttons" },
        ...[["", "すべて"], ...categories.map((key) => [key, placeCategoryLabels[key]])].map(([key, label]) =>
          h("button", { key, type: "button", "aria-pressed": category === key, onClick: () => onCategoryChange?.(key) }, label)))),
    h("p", { className: "op-count", "aria-live": "polite" }, `${visible.length}件の${collection === "restaurants" ? "お店" : "スポット"}`),
    h("div", { className: "op-card-grid" }, ...visible.map((place) => h(PlaceCard, { key: place.slug, place }))),
    !visible.length && h("p", { className: "op-empty" }, "条件に合う情報がありません。名前やエリア、カテゴリを変えてお探しください。"));
}

function PlaceDetail({ place, onAction }) {
  const food = place.collection === "restaurants";
  const instaMatch = place.instaPostUrl?.match(/instagram\.com\/(p|reel|tv)\/([^/?]+)/);
  const action = (kind) => () => onAction?.(kind, place);
  return h(React.Fragment, null,
    h("header", { className: `op-hero ${place.image ? "" : "op-hero-text"}` },
      h("div", { className: "op-hero-copy" },
        h("span", { className: "op-eyebrow" }, placeCollections[place.collection].eyebrow),
        tags(place),
        h("h1", null, place.name),
        place.genre && h("p", { className: "op-genre" }, place.genre),
        h("p", { className: "op-lead" }, place.description),
        h("div", { className: "op-actions" },
          link(getPlaceMapUrl(place), "Googleマップで見る ↗", { ...external, onClick: action("map"), className: "op-button" }),
          place.instaPostUrl && link(place.instaPostUrl, "Instagramで見る ↗", { ...external, onClick: action("instagram"), className: "op-button op-button-outline" }))),
      place.image && h("img", { src: place.image, alt: place.name, width: 900, height: 600, className: "op-hero-image" })),
    h("div", { className: "op-detail-layout" },
      h("article", null,
        h("section", { className: "op-section", id: "information" },
          h("span", { className: "op-eyebrow" }, "INFORMATION"),
          h("h2", null, food ? "営業時間・予算・アクセス" : "場所・設備・アクセス"),
          h("dl", { className: "op-facts" }, ...getPlaceFacts(place).map(([label, value]) =>
            h("div", { key: label }, h("dt", null, label),
              h("dd", null, label === "電話番号" ? link(`tel:${value.replace(/[^\d+]/g, "")}`, value, { onClick: action("phone") }) : value)))),
          h("p", { className: "op-note" }, food
            ? "営業時間・定休日・料金は変更される場合があります。最新の営業・予約状況は店舗のSNSや電話でご確認ください。"
            : "設備・駐車場の利用条件は変わる場合があります。現地の案内をご確認ください。")),
        food && place.menu && h("section", { className: "op-section" },
          h("span", { className: "op-eyebrow" }, "MENU"),
          h("h2", null, "楽しめるメニュー"),
          place.menu.food && h("p", null, place.menu.food),
          place.menu.drink && h("p", { className: "op-note" }, `ドリンク：${place.menu.drink}`)),
        instaMatch && h("section", { className: "op-section" },
          h("h2", null, "お店の雰囲気"),
          h("iframe", { src: `https://www.instagram.com/${instaMatch[1]}/${instaMatch[2]}/embed`, title: `${place.name}のInstagram投稿`, loading: "lazy", className: "op-instagram", referrerPolicy: "strict-origin-when-cross-origin" }),
          link(place.instaPostUrl, "投稿が表示されない場合はInstagramで見る ↗", { ...external, className: "op-text-link", onClick: action("instagram") })),
        h("section", { className: "op-section" },
          h("span", { className: "op-eyebrow" }, "Q & A"),
          h("h2", null, "行く前に知っておきたいこと"),
          ...getPlaceFaqs(place).map(([question, answer]) => h("details", { key: question, className: "op-faq" },
            h("summary", null, question), h("p", null, answer))))),
      h("aside", { className: "op-sidebar" },
        h("span", { className: "op-eyebrow" }, food ? "PLAN YOUR DAY" : "TODAY'S OCEAN"),
        h("h2", null, food ? "お店と、その前後の予定も" : "出発前に、今日の海を確認"),
        h("p", null, food ? "食事の前後に楽しめるビーチや夕日、天気をOcean Guideで確認できます。" : "風向き・波・うねりによって、同じ場所でも海の様子は日ごとに変わります。"),
        link("/ocean-guide", "Ocean Guideで確認する →", { className: "op-button" }),
        h("nav", { className: "op-guide-links", "aria-label": "関連ガイド" },
          ...getPlaceGuideLinks(place).map(([href, label]) => link(href, `${label} →`, { key: href }))))),
    h("section", { className: "op-related" },
      h("div", { className: "op-section-heading" },
        h("h2", null, food ? "ほかのお店も探す" : "ほかのスポットも探す"),
        link(`/ocean-guide/${place.collection}`, "一覧を見る →")),
      h("div", { className: "op-card-grid" }, ...getRelatedPlaces(place).map((item) => h(PlaceCard, { key: item.slug, place: item })))));
}

export function OceanPlaceContent({
  collection = "spots", slug, query = "", category = "", onQueryChange, onCategoryChange, onAction,
}) {
  const place = slug ? findOceanPlace(collection, slug) : null;
  const info = placeCollections[collection];
  return h("div", { className: "ocean-place" },
    h("header", { className: "op-topbar" },
      link("/ocean-guide", "Miyako Ocean Guide", { className: "op-brand" }),
      h("nav", { "aria-label": "ガイドメニュー" },
        link("/ocean-guide/spots", "スポット", { "aria-current": collection === "spots" ? "true" : undefined }),
        link("/ocean-guide/restaurants", "飲食店", { "aria-current": collection === "restaurants" ? "true" : undefined }))),
    h("main", { className: "op-main" },
      h("nav", { className: "op-breadcrumb", "aria-label": "パンくず" },
        link("/ocean-guide", "Ocean Guide"), h("span", { "aria-hidden": true }, "/"),
        slug ? link(`/ocean-guide/${collection}`, info.title) : h("span", null, info.title),
        slug && h("span", { "aria-hidden": true }, "/"),
        slug && h("span", { "aria-current": "page" }, place?.name || "ページが見つかりません")),
      slug && !place ? h("section", { className: "op-empty" },
        h("h1", null, "ページが見つかりません"), link(`/ocean-guide/${collection}`, `${info.title}の一覧へ`))
        : place ? h(PlaceDetail, { place, onAction })
          : h(PlaceDirectory, { collection, query, category, onQueryChange, onCategoryChange })),
    h("footer", { className: "op-footer" },
      link("/ocean-guide", "Miyako Ocean Guide"),
      h("p", null, "宮古島の海・天気・お店をひとつに"),
      link(collection === "spots" ? "/ocean-guide/restaurants" : "/ocean-guide/spots", collection === "spots" ? "飲食店・カフェを探す →" : "ビーチ・観光スポットを探す →")));
}
